import type { DraftRowShape } from '@/lib/draft-rows/draftRow';
import type { AnchorStepSpec } from '../anchorStepSpecs';
import type { WizardValidationContext } from '../useWizardValidationContext';
import type { AnchorImportPayload } from './buildAnchorImportPayload';

export interface CommittedAnchorDelta<TRow> {
  /** Server id per SENT create row (created and skipped alike), by row identity. */
  idByRow: Map<TRow, string>;
  /** The sent snapshot with every create row's entityId stamped. */
  stampedRows: TRow[];
}

/**
 * Sends an anchor step's classified delta and zips the server's per-row
 * results back onto the sent create rows. A create that collided with an
 * existing entity (`created: false`) is re-sent as an in-place update in a
 * follow-up request: referencing an existing entity edits it — the row's
 * changes must never be dropped by the server-side skip.
 */
export async function commitAnchorDelta<
  TData extends object,
  TRow extends DraftRowShape<TData>,
  TRequest,
  TResponse
>(config: {
  spec: AnchorStepSpec<TData, TRow, TRequest, TResponse>;
  validation: WizardValidationContext;
  payload: AnchorImportPayload<TData>;
  /** The row objects behind payload.rows, in the same order. */
  createRows: readonly TRow[];
  allRows: readonly TRow[];
  mutate: (request: TRequest) => Promise<TResponse>;
}): Promise<CommittedAnchorDelta<TRow>> {
  const { spec, validation, createRows, mutate } = config;
  const response = await mutate(spec.buildRequest(config.payload, validation));
  const results = spec.selectRowResults(response);

  const idByRow = new Map<TRow, string>();
  const collisions: Array<TData & { _id: string }> = [];
  createRows.forEach((row, index) => {
    const result = results[index];
    if (!result) return;
    idByRow.set(row, result._id);
    if (!result.created) collisions.push({ _id: result._id, ...row.data });
  });
  if (collisions.length > 0) {
    await mutate(spec.buildRequest({ rows: [], updates: collisions }, validation));
  }

  const stampedRows = config.allRows.map((row) => {
    const id = idByRow.get(row);
    return id && !row.entityId ? { ...row, entityId: id } : row;
  });
  return { idByRow, stampedRows };
}
